import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X, Phone, Instagram, Facebook, Youtube } from "lucide-react";
import { XLogo } from "@/components/site/icons/XLogo";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/our-program", label: "Our Program" },
  { to: "/team", label: "Our Team" },
  { to: "/services", label: "Bariatric Surgery" },
  { to: "/medical-weight-loss", label: "Medical Weight Loss" },
  { to: "/results", label: "Before & After" },
  { to: "/dr-fitzer-dissects-the-news", label: "Dr. Fitzer Dissects the News" },
  { to: "/resources", label: "Patient Links" },
  { to: "/search", label: "Search" },
] as const;


function HeaderControls({
  open,
  onMenuClick,
  invisible = false,
}: {
  open: boolean;
  onMenuClick: () => void;
  invisible?: boolean;
}) {
  const wrapperClasses = [
    "flex shrink-0 items-center gap-0.5 sm:gap-2",
    invisible ? "invisible pointer-events-none" : "",
  ].join(" ");

  return (
    <div className={wrapperClasses} aria-hidden={invisible || undefined}>
      <div className="hidden min-[900px]:block">
        {invisible ? null : <ThemeToggle />}
      </div>
      <button
        type="button"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        aria-controls="mobile-nav"
        onClick={onMenuClick}
        tabIndex={invisible ? -1 : undefined}
        className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md p-1.5 text-foreground/80 hover:text-brand sm:p-2 min-[900px]:hidden"
      >
        {open ? <X size={26} aria-hidden="true" /> : <Menu size={26} aria-hidden="true" />}
      </button>
    </div>
  );
}


export function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header className="relative border-b border-border/60 bg-background/90">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 sm:py-5">
        <div className="relative flex w-full items-center justify-center">
          <a
            href="tel:7037099771"
            aria-label="Call Virginia Bariatric Surgery at 703-709-9771"
            className="absolute left-0 top-1/2 -translate-y-1/2 inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md p-1.5 text-brand-logo hover:text-brand-deep sm:p-2 md:hidden"
          >
            <Phone size={20} aria-hidden="true" />
            <span className="hidden sm:inline">(703) 709-9771</span>
          </a>
          <Link
            to="/"
            className="flex min-w-0 items-center"
            onClick={() => setOpen(false)}
          >
            <img
              src="/logo.svg"
              alt="Virginia Bariatric Surgery — home"
              width={1500}
              height={321}
              className="h-[38px] w-auto max-w-full sm:h-[50px] md:h-[58px] dark:brightness-[1.55]"
            />
          </Link>
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <HeaderControls open={open} onMenuClick={() => setOpen((v) => !v)} />
          </div>
        </div>
        <nav className="hidden min-[900px]:flex flex-wrap items-center justify-center gap-x-3 gap-y-2 lg:gap-x-6 text-sm font-medium text-foreground/80">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className="whitespace-nowrap text-foreground hover:text-brand-deep transition-colors"
              activeProps={{ className: "text-brand-deep font-semibold underline decoration-brand-deep decoration-2 underline-offset-[6px]" }}
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
      <div
        id="mobile-nav"
        className={`min-[900px]:hidden border-t border-border/60 bg-background transition-[max-height] duration-300 ease-in-out ${
          open
            ? "max-h-[100vh] overflow-x-hidden overflow-y-auto"
            : "max-h-0 overflow-hidden"
        }`}
      >

        <nav
          aria-label="Mobile"
          className="mx-auto flex max-w-7xl flex-col px-6 py-2 text-base font-medium text-foreground/80"
        >
          <a
            href="https://www.instagram.com/doctorfitzer/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Dr. Matthew Fitzer on Instagram"
            tabIndex={open ? undefined : -1}
            aria-hidden={open ? undefined : true}
            className="inline-flex items-center gap-2 border-b border-border/40 py-3 text-brand-deep hover:text-brand transition-colors"
          >
            <Instagram size={18} aria-hidden="true" />
            <span>Instagram</span>
          </a>
          <a
            href="https://www.facebook.com/virginiabariatric/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Virginia Bariatric Surgery on Facebook"
            tabIndex={open ? undefined : -1}
            aria-hidden={open ? undefined : true}
            className="inline-flex items-center gap-2 border-b border-border/40 py-3 text-brand-deep hover:text-brand transition-colors"
          >
            <Facebook size={18} aria-hidden="true" />
            <span>Facebook</span>
          </a>
          <a
            href="https://www.youtube.com/@virginiabariatricsurgeryce114"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Virginia Bariatric Surgery on YouTube"
            tabIndex={open ? undefined : -1}
            aria-hidden={open ? undefined : true}
            className="inline-flex items-center gap-2 border-b border-border/40 py-3 text-brand-deep hover:text-brand transition-colors"
          >
            <Youtube size={18} aria-hidden="true" />
            <span>YouTube</span>
          </a>
          <a
            href="https://x.com/vbariatric"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Virginia Bariatric on X (Twitter)"
            tabIndex={open ? undefined : -1}
            aria-hidden={open ? undefined : true}
            className="inline-flex items-center gap-2 border-b border-border/40 py-3 text-brand-deep hover:text-brand transition-colors"
          >
            <XLogo size={19} />
          </a>

          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              onClick={() => setOpen(false)}
              tabIndex={open ? undefined : -1}
              aria-hidden={open ? undefined : true}
              className="py-3 pl-3 border-l-4 border-transparent border-b border-border/40 hover:text-brand transition-colors"
              activeProps={{ className: "text-brand-deep font-semibold border-brand-deep bg-brand-soft/30" }}
            >
              {link.label}
            </Link>
          ))}

          <LanguageSwitcher
            onNavigate={() => setOpen(false)}
            tabIndex={open ? undefined : -1}
            ariaHidden={open ? undefined : true}
            className="py-3 pl-3 border-l-4 border-transparent border-b border-border/40 last:border-b-0 hover:text-brand transition-colors"
          />


          <div className="py-3">{open ? <ThemeToggle /> : null}</div>
        </nav>
      </div>
    </header>
  );
}
